import { randomId } from "@mantine/hooks";
import { type ICol } from "src/interfaces";
import { v4 as uuidv4 } from "uuid";
import { parseGuernseyContentToUUID } from "../../components/shared/GuernseyMulti/Guernsey.utils";
import { IGuernseyRow } from "../../components/shared/GuernseyMulti/Guernsey.types";
import { alignRowsToHeaders } from "./alignDynamicCols";

let exclusives: Record<string, string> = {};
/**
 * Converts an array of columns or a single column into a UUID-based object structure.
 *
 * @param cols - The columns to convert, either as an array or a single column.
 * @param itemNested - A boolean indicating if the columns are nested within an item. Defaults to `false`.
 * @param addIndexProp - A boolean indicating if an index property should be added to each column. Defaults to `false`.
 * @param deeplyNestedCols - A boolean indicating if the columns are deeply nested. Defaults to `false`.
 * @returns A UUID-based object structure representing the columns.
 */
export const colsToUUIDObject = (
  cols: ICol[] | ICol,
  itemNested?: boolean,
  addIndexProp?: boolean,
  deeplyNestedCols?: boolean,
) => {
  let uuidObject: Record<string, object> = {};
  if (Array.isArray(cols)) {
    cols.forEach((col, index) => {
      if (!col?.content) {
        const uuid = uuidv4();
        const extendedId = uuid + "-exclusive-" + col.exclusive;
        const extendedUserId = uuid + "-exclusiveUser-" + col.exclusiveUser;

        if (
          (col.type === "item" && col.cols) ||
          (col.type === "split" && itemNested)
        ) {
          const newNestedColsWithIds = colsToUUIDObject(
            col.cols ?? [],
            true,
            false,
            deeplyNestedCols,
          );
          if (itemNested && deeplyNestedCols) {
            uuidObject = { ...uuidObject, ...newNestedColsWithIds };
          } else {
            uuidObject[uuid] = { ...col, cols: newNestedColsWithIds };
          }
        } else if (col.type === "split" || (col.type === "col" && col.cols)) {
          if ("cols" in col) {
            col.cols?.forEach((cell: ICol) => {
              const uuid = uuidv4();
              let value: any = cell.value ?? "";
              let insertedIndexForValue = null;

              if (col.type === "col" && col.cols) {
                insertedIndexForValue = Number(col.value);
              }

              if (cell.unit === "guernseyMulti" && cell.content) {
                cell.content = parseGuernseyContentToUUID(
                  cell.content as Record<string, IGuernseyRow[]>,
                );
              }

              if (cell.unit === "temperature") {
                value = {
                  value: Number(value.replace(/[^\d.]/g, "")) || null,
                  unit: "celsius",
                };
              } else if (cell.unit === "pressure") {
                value = {
                  value: Number(value.replace(/[^\d.]/g, "")) || null,
                  unit: "mbar",
                };
              }
              if (cell.cols && deeplyNestedCols) {
                const newNestedColsWithIds = colsToUUIDObject(
                  cell.cols,
                  true,
                  false,
                  deeplyNestedCols,
                );
                uuidObject = { ...uuidObject, ...newNestedColsWithIds };
              } else {
                const cellObj: any = { ...cell, value };
                if (insertedIndexForValue !== null) {
                  cellObj.insertedIndexForValue = insertedIndexForValue;
                }
                uuidObject[uuid] = cellObj;
              }
            });
          }
        } else if (
          col.unit === "bool/attach/text" &&
          ("list" in col || col.dynamicRows)
        ) {
          const items = col.list && col.list.length > 0 ? col.list : [];
          col.list = items.map((item: any) => {
            if (item.id) {
              return item;
            }
            const newItem = {
              ...item,
              id: uuidv4(),
            };
            return newItem;
          });
          uuidObject[uuid] = col;
        } else if (
          col.unit === "large/text" &&
          ("list" in col || col.dynamicRows)
        ) {
          const items = col.list && col.list.length > 0 ? col.list : [];
          col.list = items.map((item: any) =>
            typeof item === "string" ? { text: item, id: "new" } : item,
          );

          uuidObject[uuid] = col;
        } else if (col.unit === "vital_list" && "list" in col) {
          col.list = col.list && col.list.length > 0 ? col.list : [];
          uuidObject[uuid] = col;
        } else if (col.exclusive && col.value) {
          exclusives[extendedId] = col.value as string;
          uuidObject[uuid] = col;
        } else if (col.exclusiveUser && col.value) {
          exclusives[extendedUserId] = col.value as string;
          uuidObject[uuid] = col;
        } else if (col.value) {
          let value: any = col.value;
          if (typeof value === "string") {
            if (col.unit === "temperature") {
              value = {
                value: Number(value.replace(/[^\d.]/g, "")) || null,
                unit: "celsius",
              };
            } else if (col.unit === "pressure") {
              value = {
                value: Number(value.replace(/[^\d.]/g, "")) || null,
                unit: "mbar",
              };
            }
          }
          uuidObject[uuid] = { ...col, value };
        } else {
          uuidObject[uuid] = {
            ...col,
            value: "",
            ...(addIndexProp ? { index } : {}),
          };
        }
      } else if (col.content && col.unit === "guernseyMulti") {
        const uuid = uuidv4();
        uuidObject[uuid] = {
          ...col,
          content: parseGuernseyContentToUUID(
            col.content as Record<string, IGuernseyRow[]>,
          ),
        };
      } else {
        const uuid = uuidv4();
        uuidObject[uuid] = col;
        const newNestedColsWithIds = colsToUUIDObject(col.content.cols);
        const colToModify = uuidObject[uuid] as any;

        colToModify.content.cols = newNestedColsWithIds;
        uuidObject[uuid] = colToModify;
      }
    });
  } else {
    const uuid = uuidv4();
    uuidObject[uuid] = { ...cols, value: "" };
  }
  return uuidObject;
};
/**
 * Extracts NMEA values from headers and populates the provided NMEA object.
 *
 * @param headers - An array of headers to process.
 * @param nmea - A record to store extracted NMEA values.
 */
const getNmeaValues = (headers: any[], nmea: Record<string, any>) => {
  headers.forEach(h => {
    if (
      h.type === "split" &&
      h.cols &&
      Array.isArray(h.cols) &&
      h.cols.length > 0
    ) {
      return getNmeaValues(h.cols, nmea);
    }
    if (h.NMEA && h.value) {
      nmea[h.NMEA] = h.value;
    }
  });
};
/**
 * Checks the maximum level of nested headers in a given array of headers.
 *
 * @param headers - An array of headers to check.
 * @returns The maximum level of nesting in the headers.
 */
const checkDeeplyNestedHeaders = (headers: ICol[]) => {
  if (!headers || Array.isArray(headers?.[0])) return 0;
  let maxNestedLevel = 0;
  headers.forEach(header => {
    if (header.cols) {
      const nestedLevel = checkDeeplyNestedHeaders(header.cols);
      maxNestedLevel = Math.max(maxNestedLevel, nestedLevel);
    }
  });
  return maxNestedLevel + 1;
};
/**
 * Converts an array of sections into a UUID-based object structure.
 *
 * @param sections - An array of sections to convert.
 * @returns An object containing the converted sections and exclusives.
 */
export const convertToUUIDKeys = (sections: any[]): any => {
  exclusives = {};

  const mapOverRowsOrHeaders = (section: any) => {
    const isSharedSection = typeof section.shared === "string";
    if (isSharedSection) {
      section.sections = section.sections?.map((s: any) => {
        const mapped = mapOverRowsOrHeaders(s);
        return {
          ...s,
          ...mapped,
          className: randomId(),
        };
      });
      return section;
    }
    const nmea: Record<string, any> = {};
    if (section.headers) getNmeaValues(section.headers, nmea);
    let rows = [];
    const hasDeeplyNested = checkDeeplyNestedHeaders(section.headers) > 2;
    if (section.subSections) {
      return {
        ...section,
        subSections: convertToUUIDKeys(section.subSections).convertedSections,
      };
    } else if (
      section &&
      section.rows &&
      section.rows.length > 0 &&
      section.rows[0] !== "" &&
      section.rows.map
    ) {
      rows = section.rows.map((row: any) => {
        if (!Array.isArray(row) && row.cols) {
          return {
            ...row,
            cols: colsToUUIDObject(row.cols as ICol),
          };
        } else if (!Array.isArray(row) && row.items) {
          return {
            ...row,
            items: colsToUUIDObject(row.items as ICol),
          };
        } else if (
          row[0] &&
          ((!Array.isArray(row[0]) && "0" in row[0]) || Array.isArray(row[0]))
        ) {
          const subrows = Object.values(row).map((r: any) => ({
            cols: colsToUUIDObject(Object.values(r)),
          }));
          return { subrows };
        } else {
          return {
            cols: colsToUUIDObject(row),
          };
        }
      });
    } else if (
      section &&
      section.rows &&
      section.rows.length > 0 &&
      section.rows.forEach
    ) {
      if (Array.isArray(section.headers[0])) {
        section.rows.forEach(() => {
          const subrows = section.headers.map((header: any) => ({
            cols: colsToUUIDObject(header),
          }));
          rows.push({ subrows });
        });
      } else {
        section.rows.forEach(() => {
          rows.push({
            cols: colsToUUIDObject(
              section.headers,
              false,
              false,
              hasDeeplyNested,
            ),
          });
        });
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      if (section && section.rows && section.rows.length > 0) {
        rows = section.rows.map(() => ({
          cols: colsToUUIDObject(
            section.headers,
            false,
            false,
            hasDeeplyNested,
          ),
        }));
      }
    }

    alignRowsToHeaders({ headers: section.headers, rows });

    return { rows, nmea: Object.keys(nmea).length > 0 ? nmea : null };
  };

  const convertedSections = sections?.map(section => {
    const mapped = mapOverRowsOrHeaders(section);
    return {
      ...section,
      ...mapped,
      className: randomId(),
    };
  });

  return { convertedSections, exclusives };
};
